import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import AppDialog from '../../../shared/components/AppDialog';

import LabeledField from '../../../shared/components/LabeledField';
import { applyApiFieldErrors } from '../../../shared/api';
import ElectricityAssignmentDetails from './ElectricityAssignmentDetails';

import useTranslatedOptions from '../../../shared/hooks/useTranslatedOptions';

import {
  ACTIVE_FLAG_OPTIONS,
  LOCATION_TYPE,
  LOCATION_TYPE_OPTIONS,
} from '../../../shared/constants';

import {
  suggestedLocationCode,
  toOfficeForm,
  toOfficePayload,
} from '../model/officeForm';
import {
  hasAssignmentMappingChange,
  normalizeOfficeAssignment,
  SCOPE_2_APPLICATION,
} from '../model/scope2Assignment';

export default function OfficeFormDialog({
  open,
  office,
  suppliers = [],
  fiscalYear,
  saving = false,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation();

  const isEdit = Boolean(office);
  const isLocationCodeOverridden = useRef(false);

  const locationTypeOptions = useTranslatedOptions(LOCATION_TYPE_OPTIONS);

  const activeFlagOptions = useTranslatedOptions(ACTIVE_FLAG_OPTIONS);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitted },
  } = useForm({
    mode: 'onTouched',
    defaultValues: toOfficeForm(office, fiscalYear),
  });

  useEffect(() => {
    if (open) {
      isLocationCodeOverridden.current = false;
      reset(toOfficeForm(office, fiscalYear));
    }
  }, [fiscalYear, open, office, reset]);

  const locationType = watch('location_type');

  const areaValue = watch('area_m2');
  const supplierId = watch('supplier_id');
  const applicationCode = watch('application_code');
  const currentAssignment = normalizeOfficeAssignment(office, fiscalYear);
  const isAssignmentChanged = hasAssignmentMappingChange(
    {
      supplier_id: supplierId,
      application_code: applicationCode,
    },
    currentAssignment,
  );
  const catalogSupplier = suppliers.find(supplier => supplier.id === supplierId);
  const selectedSupplier = catalogSupplier
    || (supplierId
      ? {
          id: supplierId,
          registrationNo: currentAssignment.registrationNo,
          nameJa: currentAssignment.supplierName,
        }
      : null);

  // Rule L6 is a warning, not a blocker — only raise it once the user submits.
  const showAreaWarning = isSubmitted
    && locationType === LOCATION_TYPE.OFFICE
    && areaValue.trim() === '';

  const requiredRule = { required: t('common.fieldRequired') };

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(toOfficePayload(values, { currentAssignment }));
    } catch (error) {
      if (!applyApiFieldErrors(error, setError, t)) throw error;
    }
  });

  return (
    <AppDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        {t(isEdit ? 'office.form.editTitle' : 'office.form.addTitle')}
        {isEdit && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {office.location_code}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errors.root?.server?.message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.root.server.message}
          </Alert>
        )}

        <Grid
          container
          spacing={2.5}
          component="form"
          id="office-form"
          onSubmit={submit}
        >
          <Grid item xs={12} md={6}>
            <Controller
              name="location_name_en"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.locationNameEn')}
                  htmlFor="location_name_en"
                  required
                  error={errors.location_name_en?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="location_name_en"
                    size="small"
                    placeholder="Tokyo Head Office"
                    onChange={(event) => {
                      field.onChange(event);
                      const suggestion = suggestedLocationCode(event.target.value, {
                        isEdit,
                        isOverridden: isLocationCodeOverridden.current,
                      });
                      if (suggestion !== null) {
                        setValue('location_code', suggestion, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                  />
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="location_code"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.locationCode')}
                  htmlFor="location_code"
                  required
                  error={errors.location_code?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="location_code"
                    size="small"
                    disabled={isEdit}
                    placeholder="Mita"
                    onChange={(event) => {
                      if (!isEdit) isLocationCodeOverridden.current = true;
                      field.onChange(event);
                    }}
                  />
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="location_name_jp"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.locationNameJp')}
                  htmlFor="location_name_jp"
                  required
                  error={errors.location_name_jp?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="location_name_jp"
                    size="small"
                    placeholder="東京三田オフィス"
                  />
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="location_address"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.locationAddress')}
                  htmlFor="location_address"
                  required
                  error={errors.location_address?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="location_address"
                    size="small"
                    multiline
                    minRows={2}
                  />
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="location_type"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.locationType')}
                  htmlFor="location_type"
                  required
                  error={errors.location_type?.message}
                >
                  <Select
                    {...field}
                    id="location_type"
                    size="small"
                  >
                    {locationTypeOptions.map(option => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="area_m2"
              control={control}
              rules={{
                validate: (value) => {
                  if (value.trim() === '') return true;
                  return Number(value) >= 0 || t('office.field.areaInvalid');
                },
              }}
              render={({ field }) => (
                <LabeledField
                  label={t('office.field.areaM2')}
                  htmlFor="area_m2"
                  error={errors.area_m2?.message}
                  helper={showAreaWarning ? t('office.form.areaWarning') : undefined}
                  helperColor="warning.main"
                >
                  <OutlinedInput
                    {...field}
                    id="area_m2"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography
              variant="subtitle2"
              fontWeight={750}
              sx={{ mt: 1.5 }}
            >
              {t('office.assignment.title')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {t('office.assignment.subtitle')}
            </Typography>
          </Grid>

          {suppliers.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {t('office.assignment.supplierCatalogEmpty')}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Controller
              name="supplier_id"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.assignment.supplier')}
                  htmlFor="supplier_id"
                  required
                  error={errors.supplier_id?.message}
                >
                  <Select
                    {...field}
                    id="supplier_id"
                    size="small"
                  >
                    {selectedSupplier && !catalogSupplier && (
                      <MenuItem value={selectedSupplier.id} disabled>
                        {selectedSupplier.registrationNo || '—'} · {selectedSupplier.nameJa
                          || t('office.assignment.supplierUnavailable')}
                      </MenuItem>
                    )}
                    {suppliers.map(supplier => (
                      <MenuItem
                        key={supplier.id}
                        value={supplier.id}
                      >
                        {supplier.registrationNo} · {supplier.nameJa}
                      </MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="application_code"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField
                  label={t('office.assignment.application')}
                  htmlFor="application_code"
                  required
                  error={errors.application_code?.message}
                >
                  <Select
                    {...field}
                    id="application_code"
                    size="small"
                  >
                    {Object.values(SCOPE_2_APPLICATION).map(value => (
                      <MenuItem
                        key={value}
                        value={value}
                      >
                        {t(`office.assignment.applicationOption.${value}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>

          <ElectricityAssignmentDetails
            control={control}
            errors={errors}
            requiredRule={requiredRule}
            isChanged={isAssignmentChanged}
          />

          <Grid item xs={12} md={6}>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <LabeledField
                  label={t('common.status')}
                  htmlFor="is_active"
                >
                  <Select
                    {...field}
                    id="is_active"
                    size="small"
                  >
                    {activeFlagOptions.map(option => (
                      <MenuItem
                        key={String(option.value)}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
        >
          {t('common.cancel')}
        </Button>

        <Button
          type="submit"
          form="office-form"
          variant="contained"
          disabled={saving}
        >
          {t(
            isEdit
              ? (saving ? 'common.updating' : 'common.update')
              : (saving ? 'common.creating' : 'common.create'),
          )}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}